import { useAuthStore } from '@/features/auth/auth-store';
import { deriveAdminPermissionsFromRolesArray } from '@/features/auth/auth.logic';
import {
  ProvisioningStatus,
  ServiceTier,
} from '@/features/provisioning/provisioning.type';
import { getConfig } from '@/utils/config';
import { decodeUmaTicket } from '@/utils/jwt';
import { localStorageKeys } from '@/utils/local-storage';
import {
  generateCodeChallenge,
  generateCodeVerifier,
} from '@/utils/pkce';
import { generateRandomString } from '@/utils/strings';
import { useCallback, useRef, useState } from 'react';

type UseAuth = {
  accessToken: string | undefined;
  accessTokenExpired: boolean;
  isAuthenticated: boolean;
  signInRedirect: () => Promise<void>;
  handleKeycloakRedirect: () => Promise<void>;
  refreshTokens: () => Promise<void>;
  getUserInfo: (accessToken: string) => Promise<UserInfo | undefined>;
  logout: () => void;
  umaTicketDecoded: UmaTicketDecoded | undefined;
  requestUmaTicket: () => Promise<UmaTicketResponse | undefined>;
};

const stateStorageKey = 'embraceable-ai-auth-state';
const codeVerifierStorageKey = 'embraceable-ai-auth-code-verifier';

type CustomAttribute =
  | {
      key: 'service_tier';
      value: ServiceTier;
    }
  | {
      key: 'provisioning_state';
      value: ProvisioningStatus;
    };

type UserInfo = {
  custom_attributes?: Array<CustomAttribute>;
  email: string;
  logo_url?: string;
  sub: string;
  token_limit?: number;
  token_limit_period?: 'daily' | 'monthly';
};

type Tokens = {
  access_token: string;
  refresh_token: string;
  expires_in: number;
};

type UmaTicketResponse = {
  access_token: string;
};

type UmaTicketDecoded = {
  permissions: Permission[];
  roles: string[];
};

export type Permission = {
  rsname: string;
};

export const authConfig = {
  get accountSettingsUrl() {
    return `${getConfig().keycloak.baseUrl}/account`;
  },
  get authorityUrl() {
    return getConfig().keycloak.baseUrl;
  },
  get clientId() {
    return getConfig().keycloak.clientId;
  },
  get redirectUri() {
    return `${window.location.origin}${window.location.pathname}`;
  },
  get tokenEndpoint() {
    return `${getConfig().keycloak.baseUrl}/protocol/openid-connect/token`;
  },
} as const;

export function useAuth(): UseAuth {
  const {
    accessToken,
    accessTokenExpiresAt,
    refreshToken,
    setAccessToken,
    setRefreshToken,
    setAccessTokenExpiresAt,
    setAdminPermissions,
    organizationId,
  } = useAuthStore();

  const [umaTicketDecoded, setUmaTicketDecoded] = useState<
    UmaTicketDecoded | undefined
  >(undefined);

  const isRefreshing = useRef(false);

  const accessTokenExpired =
    accessToken !== undefined &&
    accessTokenExpiresAt !== undefined &&
    accessTokenExpiresAt < Date.now() &&
    refreshToken !== undefined;

  const isAuthenticated =
    accessToken !== undefined &&
    accessTokenExpiresAt !== undefined &&
    accessTokenExpiresAt > Date.now();

  const signInRedirect = async () => {
    const state = generateRandomString(32);
    const codeVerifier = generateCodeVerifier();
    const codeChallenge = await generateCodeChallenge(codeVerifier);

    // Store code verifier in session storage to use it later
    sessionStorage.setItem(codeVerifierStorageKey, codeVerifier);
    sessionStorage.setItem(stateStorageKey, state);

    const params = new URLSearchParams({
      client_id: authConfig.clientId,
      redirect_uri: authConfig.redirectUri,
      response_type: 'code',
      scope: 'openid profile offline_access',
      state: state,
      code_challenge: codeChallenge,
      code_challenge_method: 'S256',
    });

    const authUrl = `${authConfig.authorityUrl}/protocol/openid-connect/auth?${params.toString()}`;

    window.location.href = authUrl;
  };

  const handleAuthCallback = async (
    code: string,
    codeVerifier: string,
  ) => {
    // Exchange code for tokens
    const response = await fetch(authConfig.tokenEndpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: new URLSearchParams({
        client_id: authConfig.clientId,
        code_verifier: codeVerifier,
        code: code,
        grant_type: 'authorization_code',
        redirect_uri: authConfig.redirectUri,
      }),
    });

    if (!response.ok) {
      throw new Error('Failed to exchange code for tokens');
    }

    const tokens = (await response.json()) as Tokens;

    setAccessTokenExpiresAt(Date.now() + tokens.expires_in * 1000);
    setAccessToken(tokens.access_token);
    setRefreshToken(tokens.refresh_token);
  };

  const handleKeycloakRedirect = async () => {
    try {
      const params = new URLSearchParams(window.location.search);

      const code = params.get('code');
      if (!code) {
        throw new Error('No code found in URL');
      }

      const state = params.get('state');
      if (!state) {
        throw new Error('No state found in URL');
      }

      const savedState = sessionStorage.getItem(stateStorageKey);
      if (!savedState) {
        throw new Error('No saved state found in session storage');
      }

      if (state !== savedState) {
        throw new Error('State mismatch');
      }

      const codeVerifier = sessionStorage.getItem(
        codeVerifierStorageKey,
      );
      if (!codeVerifier) {
        throw new Error('No code verifier found in session storage');
      }

      // Clean up session storage
      sessionStorage.removeItem(stateStorageKey);
      sessionStorage.removeItem(codeVerifierStorageKey);

      await handleAuthCallback(code, codeVerifier);

      // Remove code from URL without triggering a refresh
      window.history.replaceState(
        {},
        document.title,
        window.location.pathname,
      );
    } catch (error) {
      console.error('Failed to handle auth redirect:', error);
      sessionStorage.removeItem(stateStorageKey);
      sessionStorage.removeItem(codeVerifierStorageKey);
      window.location.href = '/';
    }
  };

  const getUserInfo = useCallback(async (accessToken: string) => {
    try {
      const response = await fetch(
        `${authConfig.authorityUrl}/protocol/openid-connect/userinfo`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
            Authorization: `Bearer ${accessToken}`,
          },
        },
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const userInfo = (await response.json()) as UserInfo;

      return userInfo;
    } catch (error) {
      console.error('Failed to get user info:', error);
    }
  }, []);

  const refreshTokens = useCallback(async () => {
    if (!refreshToken) {
      throw new Error('No refresh token available');
    }

    if (isRefreshing.current) {
      return;
    }

    try {
      isRefreshing.current = true;
      const response = await fetch(authConfig.tokenEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams({
          client_id: authConfig.clientId,
          grant_type: 'refresh_token',
          refresh_token: refreshToken,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const tokens = (await response.json()) as Tokens;

      setAccessTokenExpiresAt(Date.now() + tokens.expires_in * 1000);
      setAccessToken(tokens.access_token);
      setRefreshToken(tokens.refresh_token);
    } catch (error) {
      console.error('Failed to refresh token:', error);
      // If refresh fails, redirect to login
      await signInRedirect();
    } finally {
      isRefreshing.current = false;
    }
  }, [
    refreshToken,
    setAccessToken,
    setRefreshToken,
    setAccessTokenExpiresAt,
  ]);

  const logout = useCallback(async () => {
    try {
      await fetch(
        `${authConfig.authorityUrl}/protocol/openid-connect/logout`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          body: new URLSearchParams({
            client_id: authConfig.clientId,
            refresh_token: refreshToken || '',
          }),
        },
      );

      localStorage.removeItem(localStorageKeys.auth);
      sessionStorage.removeItem(codeVerifierStorageKey);
      sessionStorage.removeItem(stateStorageKey);
    } catch (error) {
      console.error('Error during logout:', error);
    } finally {
      window.location.reload();
    }
  }, [accessToken, refreshToken]);

  const requestUmaTicket = useCallback(async (): Promise<
    UmaTicketResponse | undefined
  > => {
    if (!accessToken) {
      console.error('No access token available');
      return;
    }

    try {
      const response = await fetch(authConfig.tokenEndpoint, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams({
          audience: 'resource-server',
          grant_type: 'urn:ietf:params:oauth:grant-type:uma-ticket',
        }),
      });

      if (!response.ok) {
        console.error('UMA ticket request failed:');
      }

      const umaTicket = (await response.json()) as UmaTicketResponse;

      try {
        const decodedUmaTicketAccessToken = decodeUmaTicket(
          umaTicket.access_token,
        );

        setAdminPermissions(
          deriveAdminPermissionsFromRolesArray(
            organizationId ?? '',
            decodedUmaTicketAccessToken.roles,
          ),
        );
        setUmaTicketDecoded(decodedUmaTicketAccessToken);

        return umaTicket;
      } catch (error) {
        console.error('Failed to parse UMA ticket:', error);
      }
    } catch (error) {
      console.error('Failed to request UMA ticket:', error);
    }
  }, [
    accessToken,
    setUmaTicketDecoded,
    organizationId,
    setAdminPermissions,
  ]);

  return {
    accessToken,
    accessTokenExpired,
    isAuthenticated,
    signInRedirect,
    handleKeycloakRedirect,
    refreshTokens,
    getUserInfo,
    logout,
    umaTicketDecoded,
    requestUmaTicket,
  };
}
